import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConsentLinkRow from './ConsentLinkRow.tsx';
import useAdminConsentLink from './useAdminConsentLink.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import { HAPPY } from '../../../core/components/worki/Pose.tsx';
import Worki from '../../../core/components/worki/Worki.tsx';
import classnames from '../../../core/utils/classnames.tsx';

const ChevronIcon = withIcon(faChevronDown);

const StepBadge = ({ active, children }: { active?: boolean; children: React.ReactNode }) => (
    <span
        className={classnames(
            'flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-semibold',
            active ? 'bg-surface-brand-strong-default text-content-white' : 'bg-surface-strong text-content-medium'
        )}
    >
        {children}
    </span>
);

const AdminConsentSetupCard = () => {
    const { t } = useTranslation('mailboxes');
    const [personalExpanded, setPersonalExpanded] = useState(false);
    const sharedLink = useAdminConsentLink(false);
    // the personal link is only fetched once its section is opened
    const personalLink = useAdminConsentLink(true, personalExpanded);

    return (
        <div className="flex flex-1 flex-col items-center gap-6 px-6 py-8 justify-center">
            <div className="flex flex-col items-center gap-3">
                <Worki pose={HAPPY} size={110} trackMouse="page" />
                <h2 className="m-0 text-lg font-semibold text-content-strong">{t('overview.setup.title')}</h2>
            </div>

            <div className="flex w-full max-w-2xl flex-col gap-8 rounded-lg border border-solid border-line-weak bg-surface-weak p-8">
                <div className="flex gap-4">
                    <StepBadge active>1</StepBadge>
                    <div className="flex min-w-0 flex-1 flex-col gap-4">
                        <div>
                            <p className="m-0 text-sm font-semibold text-content-strong">
                                {t('overview.setup.step1.title')}
                            </p>
                            <p className="m-0 text-sm text-content-medium">{t('overview.setup.step1.description')}</p>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-content-medium">
                                {t('overview.setup.step1.sharedLabel')}
                            </span>
                            <ConsentLinkRow link={sharedLink} />
                        </div>

                        <div>
                            <button
                                type="button"
                                onClick={() => setPersonalExpanded(!personalExpanded)}
                                className="flex items-center gap-1.5 border-none bg-transparent p-0 text-xs font-medium text-content-medium cursor-pointer hover:text-content-strong"
                            >
                                {t('overview.setup.step1.personalLabel')}
                                <ChevronIcon
                                    className={classnames('transition-transform', personalExpanded && 'rotate-180')}
                                />
                            </button>
                            {personalExpanded && (
                                <div className="mt-3 flex flex-col gap-3">
                                    <p className="m-0 text-sm text-content-medium">
                                        {t('overview.setup.step1.personalDescription')}
                                    </p>
                                    <ConsentLinkRow link={personalLink} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 opacity-50">
                    <StepBadge>2</StepBadge>
                    <div>
                        <p className="m-0 text-sm font-semibold text-content-strong">
                            {t('overview.setup.step2.title')}
                        </p>
                        <p className="m-0 text-sm text-content-medium">{t('overview.setup.step2.description')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminConsentSetupCard;
